/**
 * @file Contexte de synchronisation de la hauteur de l'iframe avec la page hôte.
 * @module widgets.common.contexts.IframeResizeContext
 *
 * Le widget embarqué ne connaît pas sa hauteur d'affichage : elle lui est imposée
 * par l'iframe. Il mesure donc son contenu et la poste au script hôte, qui
 * l'applique sur l'iframe. La mesure est relancée à la fois par des appels
 * explicites des composants (`updateHeight`, `forceResize`) et par un observateur
 * de mutations qui rattrape les contenus arrivant en asynchrone.
 */
import React, {createContext, useContext, useRef, useState, useEffect, useCallback} from 'react';
import {isInIframe} from '../../../utils/iframeUtils';
import {logger} from '../../../utils/logger';
import {TIMING} from '../../../constants/timing';

// Créer le contexte
const IframeResizeContext = createContext(null);

/**
 * Provider pour le contexte de redimensionnement de l'iframe
 * @param {object} props
 * @param {React.ReactNode} props.children
 * @param {string} [props.mode]
 * @param {string} [props.instanceId]
 * @param {number} [props.minHeight]
 * @param {number} [props.maxHeight]
 * @param {number} [props.debounceDelay]
 * @return {JSX.Element} The provider component wrapping children
 */
export function IframeResizeProvider({
  children,
  mode = 'fixed',
  instanceId = 'default-instance',
  minHeight = 400,
  maxHeight = 800,
  debounceDelay = 200,
}) {
  const containerRef = useRef(null);
  const lastSentHeightRef = useRef(null); // ✅ Initialiser à null
  const resizeInProgressRef = useRef(false);
  const timeoutRef = useRef(null);
  const retryTimeoutRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const embedded = isInIframe();

  // Fonction pour mesurer la hauteur RÉELLE (VERSION SIMPLE)
  const measureHeight = useCallback(() => {
    if (!containerRef.current) return minHeight;

    try {
      const element = containerRef.current;

      // ✅ FORCER le container à avoir la hauteur auto pour mesurer correctement
      const originalHeight = element.style.height;
      const originalMaxHeight = element.style.maxHeight;

      // Temporairement enlever les contraintes de hauteur
      element.style.height = 'auto';
      element.style.maxHeight = 'none';

      // Forcer un reflow pour que les changements prennent effet
      element.offsetHeight;

      // ✅ MAINTENANT mesurer la vraie hauteur nécessaire
      const contentHeight = element.scrollHeight;

      // Restaurer les styles originaux
      element.style.height = originalHeight;
      element.style.maxHeight = originalMaxHeight;

      // ✅ Ajouter juste une petite marge de sécurité
      const finalHeight = contentHeight + 20;

      return Math.min(Math.max(finalHeight, minHeight), maxHeight);
    } catch (error) {
      logger.error('Erreur mesure hauteur iframe:', error);
      return minHeight;
    }
  }, [minHeight, maxHeight]);

  // ✅ Utiliser useRef pour éviter les re-créations de sendResizeMessage
  const sendResizeMessageRef = useRef();

  // Fonction pour envoyer le message de redimensionnement
  const sendResizeMessage = useCallback(() => {
    if (!embedded || !containerRef.current || !window.parent || isLoading) {
      return;
    }

    // Un envoi vient d'avoir lieu : replanifier plutôt qu'abandonner, sinon une
    // rafale de mutations tombant dans la fenêtre de verrou perd sa mesure.
    if (resizeInProgressRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = setTimeout(() => {
        sendResizeMessageRef.current?.();
      }, TIMING.RESIZE_PROGRESS_RESET);
      return;
    }

    const height = measureHeight();

    // Éviter les mises à jour inutiles (tolérance de 10px)
    const lastHeight = lastSentHeightRef.current || 0;
    if (lastHeight > 0 && Math.abs(lastHeight - height) < 10) {
      return;
    }

    resizeInProgressRef.current = true;

    try {
      window.parent.postMessage({
        type: 'resize',
        height,
        instanceId,
      }, '*');

      lastSentHeightRef.current = height;
    } catch (error) {
      logger.warn('Erreur envoi message resize:', error);
    } finally {
      setTimeout(() => {
        resizeInProgressRef.current = false;
      }, TIMING.RESIZE_PROGRESS_RESET);
    }
  }, [embedded, instanceId, measureHeight, isLoading]);

  // Stocker la référence pour éviter les re-créations
  sendResizeMessageRef.current = sendResizeMessage;

  // Fonction debounce
  const debouncedSendResize = useCallback(() => {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      sendResizeMessageRef.current?.();
    }, debounceDelay);
  }, [debounceDelay]);

  // Fonction pour déclencher un resize manuel (appelée depuis les composants)
  const updateHeight = useCallback(() => {
    if (!embedded) return;

    setTimeout(() => {
      sendResizeMessageRef.current?.();
    }, TIMING.UPDATE_HEIGHT_DELAY);
  }, [embedded]);

  // Fonction pour forcer un resize immédiat
  const forceResize = useCallback(() => {
    if (embedded) {
      sendResizeMessageRef.current?.();
    }
  }, [embedded]);

  // Fonctions pour gérer le chargement
  const startLoading = useCallback(() => {
    setIsLoading(true);
  }, []);

  const endLoading = useCallback(() => {
    setIsLoading(false);
    if (embedded) {
      setTimeout(() => {
        sendResizeMessageRef.current?.();
      }, TIMING.END_LOADING_DELAY);
    }
  }, [embedded]);

  // Resize initial (une fois au montage)
  useEffect(() => {
    if (embedded && containerRef.current) {
      const timer = setTimeout(() => {
        sendResizeMessageRef.current?.();
      }, TIMING.INITIAL_RESIZE_DELAY);

      return () => clearTimeout(timer);
    }
  }, [embedded]);

  // Filet de sécurité : toute croissance du contenu relance la mesure, sans que
  // chaque étape ait à penser à appeler updateHeight(). L'observation porte sur
  // document.body et non sur containerRef : la ref de mesure est remontée sur des
  // nœuds différents selon l'état (chargement, erreur, contenu) et un observateur
  // attaché au nœud précédent cesserait de voir les mutations.
  useEffect(() => {
    if (!embedded || typeof MutationObserver === 'undefined') return undefined;

    const observer = new MutationObserver(() => {
      debouncedSendResize();
    });
    observer.observe(document.body, {childList: true, subtree: true, characterData: true});

    // Seul un vrai changement de largeur de la page hôte justifie un recalcul :
    // la hauteur appliquée par le parent déclenche elle-même un événement resize
    // dans l'iframe, qui reboucherait sur une nouvelle mesure.
    let lastWidth = window.innerWidth;
    const handleWindowResize = () => {
      if (window.innerWidth === lastWidth) return;
      lastWidth = window.innerWidth;
      debouncedSendResize();
    };
    window.addEventListener('resize', handleWindowResize);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', handleWindowResize);
    };
  }, [embedded, debouncedSendResize]);

  // Nettoyage des timeouts
  useEffect(() => {
    return () => {
      clearTimeout(timeoutRef.current);
      clearTimeout(retryTimeoutRef.current);
    };
  }, []);

  const contextValue = {
    containerRef,
    updateHeight,
    forceResize,
    sendResizeMessage,
    debouncedSendResize,
    startLoading,
    endLoading,
    isLoading,
    embedded,
    mode,
  };

  return (
    <IframeResizeContext.Provider value={contextValue}>
      {children}
    </IframeResizeContext.Provider>
  );
}

/**
 * Hook pour utiliser le contexte de redimensionnement
 * @return {object} Le contexte de redimensionnement de l'iframe
 */
export function useIframeResize() {
  const context = useContext(IframeResizeContext);

  if (context === null) {
    throw new Error('useIframeResize doit être utilisé à l\'intérieur d\'un IframeResizeProvider');
  }

  return context;
}
